import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { DesktopLayout } from "../../components/DesktopLayout";
import { Repeat, RefreshCw, Clock, Check, X, CheckCircle2 } from "lucide-react";

const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";
const POLL_INTERVAL = 30000;

const formatDataHora = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function PassagemPlantao() {
  const [passagens, setPassagens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [fotoAmpliada, setFotoAmpliada] = useState(null);

  const carregar = useCallback(async (mostrarSpinner = false) => {
    if (mostrarSpinner) setAtualizando(true);

    const { data, error } = await supabase
      .from("liberty_passagem_turno")
      .select("*")
      .eq("empresa_id", EMPRESA_ID)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      console.error("[PassagemPlantao] Supabase:", error.message);
    } else {
      setPassagens(data || []);
    }

    setUltimaAtualizacao(new Date());
    setLoading(false);
    if (mostrarSpinner) setAtualizando(false);
  }, []);

  useEffect(() => {
    carregar();
    const id = setInterval(() => carregar(), POLL_INTERVAL);
    return () => clearInterval(id);
  }, [carregar]);

  return (
    <DesktopLayout
      title="Passagem de Plantão"
      subtitle="Registros de encerramento de turno com foto e material"
      actions={
        <div className="flex items-center gap-3">
          <button
            onClick={() => carregar(true)}
            disabled={atualizando}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-300 text-xs font-medium hover:bg-white/10 hover:text-white transition disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${atualizando ? "animate-spin" : ""}`} />
            Atualizar
          </button>

          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <span className="relative inline-flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-emerald-500 live-dot" />
              <span className="relative w-2 h-2 rounded-full bg-emerald-500" />
            </span>
            <span className="text-xs font-medium text-emerald-300">Ao vivo</span>
          </div>
        </div>
      }
    >
      {/* Header */}
      <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-5 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="tiny-label text-slate-500">Encerramento de turno</p>
          <h2 className="text-xl font-bold mt-1">Passagens de ponto registradas</h2>
          <p className="text-sm text-slate-400 mt-1">
            Fotos, material e observações de cada vigilante ao encerrar o plantão.
          </p>
        </div>

        <div className="flex flex-col items-start md:items-end gap-2">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-500/10 border border-blue-500/30">
            <Repeat className="w-4 h-4 text-blue-300" />
            <span className="text-lg font-bold text-blue-300">
              {loading ? "…" : passagens.length}
            </span>
            <span className="text-xs text-blue-400">passagens</span>
          </div>
          {ultimaAtualizacao && (
            <p className="text-[11px] text-slate-500 font-mono-data">
              Atualizado às{" "}
              {ultimaAtualizacao.toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </p>
          )}
        </div>
      </div>

      {loading && (
        <p className="text-sm text-slate-500 italic text-center py-12">
          Carregando passagens…
        </p>
      )}

      {!loading && passagens.length === 0 && (
        <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-10 text-center">
          <Repeat className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="font-semibold text-slate-400">Nenhuma passagem registrada</p>
          <p className="text-sm text-slate-500 mt-1">
            Aguardando registro de passagem de ponto pelos vigilantes.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {passagens.map((p) => {
          const material = Array.isArray(p.material_confirmado) ? p.material_confirmado : [];
          return (
            <article
              key={p.id}
              className="rounded-xl border border-white/5 bg-[#0a1128] overflow-hidden hover:border-white/10 transition"
            >
              {/* Cabeçalho do card */}
              <div className="p-5 flex items-start gap-4">
                {/* Foto miniatura */}
                {p.foto_url ? (
                  <button
                    onClick={() => setFotoAmpliada(p.foto_url)}
                    className="w-16 h-16 rounded-xl overflow-hidden border border-white/10 shrink-0 hover:border-white/30 transition active:scale-95"
                    title="Ampliar foto"
                  >
                    <img src={p.foto_url} alt="assunção" className="w-full h-full object-cover" />
                  </button>
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-white/5 border border-white/10 shrink-0 flex items-center justify-center">
                    <span className="text-2xl">📷</span>
                  </div>
                )}

                {/* Dados do vigilante */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-semibold text-white">
                        {p.de_vigilante_nome || "Vigilante"}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                        {p.matricula && (
                          <p className="text-xs text-slate-400">
                            Matrícula: <span className="text-slate-200 font-mono">{p.matricula}</span>
                          </p>
                        )}
                        {p.tipo_plantao && (
                          <p className="text-xs text-slate-400">
                            Plantão:{" "}
                            <span className={`font-semibold ${p.tipo_plantao === "noturno" ? "text-blue-300" : "text-amber-300"}`}>
                              {p.tipo_plantao}
                            </span>
                          </p>
                        )}
                      </div>
                      {(p.horario_inicio || p.horario_fim) && (
                        <p className="text-xs text-slate-500 mt-0.5">
                          Turno: {p.horario_inicio || "—"} às {p.horario_fim || "—"}
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 font-mono-data shrink-0 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDataHora(p.created_at)}
                    </p>
                  </div>

                  {/* Título + prioridade */}
                  {p.titulo && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-100">"{p.titulo}"</p>
                      {p.prioridade && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          p.prioridade === "alta"  ? "bg-red-500/20 text-red-300" :
                          p.prioridade === "media" ? "bg-amber-500/20 text-amber-300" :
                                                     "bg-emerald-500/20 text-emerald-300"
                        }`}>
                          {p.prioridade === "alta" ? "Alta" : p.prioridade === "media" ? "Média" : "Baixa"}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Material confirmado */}
              {material.length > 0 && (
                <div className="px-5 pb-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
                    Material confirmado ({material.length} itens)
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {material.map((item) => (
                      <span
                        key={item}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-[11px]"
                      >
                        <Check className="w-3 h-3 shrink-0" />
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Descrição */}
              {p.descricao && (
                <div className="px-5 pb-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Descrição</p>
                  <p className="text-sm text-slate-300 leading-relaxed bg-white/5 rounded-xl px-4 py-3">{p.descricao}</p>
                </div>
              )}

              {/* Observações */}
              {p.observacoes && (
                <div className="px-5 pb-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Observações</p>
                  <p className="text-sm text-slate-300 leading-relaxed bg-white/5 rounded-xl px-4 py-3">{p.observacoes}</p>
                </div>
              )}

              {/* Status de recebimento */}
              <div className={`mx-5 mb-4 px-4 py-3 rounded-xl flex items-center gap-2 ${
                p.recebido_em ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-white/5 border border-white/10"
              }`}>
                {p.recebido_em ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-emerald-300">Recebido por {p.recebido_por_nome || "—"}</p>
                      <p className="text-[11px] text-slate-500">{formatDataHora(p.recebido_em)}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <Clock className="w-4 h-4 text-slate-500 shrink-0" />
                    <p className="text-xs text-slate-500">Aguardando recebimento{p.para_vigilante_nome ? ` por ${p.para_vigilante_nome}` : ""}</p>
                  </>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {/* Foto ampliada */}
      {fotoAmpliada && (
        <div
          className="fixed inset-0 z-[999] bg-black/90 flex items-center justify-center p-6"
          onClick={() => setFotoAmpliada(null)}
        >
          <button
            onClick={() => setFotoAmpliada(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={fotoAmpliada}
            alt="foto ampliada"
            className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </DesktopLayout>
  );
}
